import { Inject, Injectable } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import { Observable } from 'rxjs';
import {
  ShopActivateSubscriberGQL,
  ShopCreateSubscriberGQL,
  ShopPauseSubscriberGQL,
  ShopSubscriberDeclarationGQL,
  ShopSubscriberGQL,
  ShopSubscribersGQL,
  ShopUpdateSubscriberAddressGQL,
  ShopUpdateSubscriberPaymentMethodGQL,
} from './gql/shop-subscriber.gql.generated';
import { AUTHORIZATION_HEADER_PROVIDER, AuthorizationHeaderProvider } from './types/shop-auth';
import {
  IShopActivateSubscriberMutation,
  IShopCreateSubscriberMutation,
  IShopPauseSubscriberMutation,
  IShopSubscriber,
  IShopSubscriberAgreementDeclaration,
  IShopSubscriberDeclarationMutationVariables,
  IShopSubscriberQuery,
  IShopSubscribersQuery,
  IShopUpdateSubscriberAddressMutation,
  IShopUpdateSubscriberPaymentMethodMutation,
} from './types/shop-subscriber';

@Injectable()
export class ShopSubscriberService {
  constructor(
    private shopCreateSubscriberGQL: ShopCreateSubscriberGQL,
    private shopSubscriberGQL: ShopSubscriberGQL,
    private shopSubscribersGQL: ShopSubscribersGQL,
    private shopUpdateSubscriberPaymentMethodGQL: ShopUpdateSubscriberPaymentMethodGQL,
    private shopUpdateSubscriberAddressGQL: ShopUpdateSubscriberAddressGQL,
    private shopSubscriberDeclarationGQL: ShopSubscriberDeclarationGQL,
    private shopActivateSubscriberGQL: ShopActivateSubscriberGQL,
    private shopPauseSubscriberGQL: ShopPauseSubscriberGQL,
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
  ) {
    if (!authHeader) throw new Error('authHeader Provider is required');
  }

  createSubscriber(query: IShopCreateSubscriberMutation): Observable<{ id: number }> {
    return this.shopCreateSubscriberGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateSubscriber));
  }

  fetchSubscriber(query: IShopSubscriberQuery): Observable<IShopSubscriber> {
    return this.shopSubscriberGQL
      .fetch(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopSubscriber));
  }

  fetchSubscribers(query: IShopSubscribersQuery): Observable<IShopSubscriber[]> {
    return this.shopSubscribersGQL
      .fetch(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopSubscribers));
  }

  updateSubscriberPaymentMethod(
    query: IShopUpdateSubscriberPaymentMethodMutation,
  ): Observable<{ id: number }> {
    return this.shopUpdateSubscriberPaymentMethodGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateSubscriberPaymentMethod));
  }

  updateSubscriberAddress(query: IShopUpdateSubscriberAddressMutation): Observable<{ id: number }> {
    return this.shopUpdateSubscriberAddressGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateSubscriberAddress));
  }

  updateSubscriberDeclaration(
    args: IShopSubscriberDeclarationMutationVariables,
  ): Observable<IShopSubscriberAgreementDeclaration[]> {
    return this.shopSubscriberDeclarationGQL
      .mutate(args, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopSubscriberDeclaration));
  }

  activateSubscriber(query: IShopActivateSubscriberMutation): Observable<IShopSubscriber> {
    return this.shopActivateSubscriberGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopActivateSubscriber));
  }

  pauseSubscriber(query: IShopPauseSubscriberMutation): Observable<{ id: number }> {
    return this.shopPauseSubscriberGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopPauseSubscriber));
  }
}
